import { useEffect, useRef, useState } from 'react'
import {
  CheckoutItem,
  CheckoutItemModification,
  ModificationType,
  PurchaseProductDetails,
} from '@ancon/wildcat-types'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import CloseIcon from '@ancon/wildcat-ui/shared/icons/close.svg'
import isDetailedProductVariantOutOfStock from '@ancon/wildcat-utils/inventory/isDetailedProductVariantOutOfStocks'

import useAppSelector from '../../../store/hooks/useAppSelector'
import { productDetailsSelector } from '../store/productSelectors'
import { outletSelectedOutletIdSelector } from '../../outlet/store/outletSelector'
import { ConfigureProductFormState } from '../types'
import Button from '../../app/components/Button'
import {
  checkoutCurrentCheckoutOutletIdSelector,
  checkoutCurrentCheckoutSelectedItemSelector,
} from '../../checkout/store/checkoutSelectors'
import { ProductConfigureAccordionId } from '../constants'
import { AppTestIds } from '../../app/constants'
import isAddonGroupRequired from '../utils/isAddonGroupRequired'
import getFirstInvalidProductSection from '../utils/getFirstInvalidProductSection'
import getInitialActiveAccordionId from '../utils/getInitialActiveAccordionId'

import ProductMetaForm from './ProductMetaForm'
import ProductVariantsForm from './ProductVariantsForm'
import ProductIngredientsForm from './ProductIngredientsForm'
import ProductAddonGroupsForm from './addonGroups/ProductAddonGroupsForm'
import ProductFooterForm from './ProductFooterForm'
import styles from './ProductConfigureForm.module.scss'

function getInitialVariantState(
  productDetails: PurchaseProductDetails,
  outletId: string,
  selectedCheckoutItem: CheckoutItem | undefined,
) {
  const { variants, mandatoryToPickVariant } = productDetails

  const inStockVariants = variants.filter(
    variant => !isDetailedProductVariantOutOfStock(variant, outletId),
  )

  const mainInStockVariant = inStockVariants.find(
    variant => variant.isMainProductVariant,
  )

  const fallBackVariant = variants.find(variant => variant.isMainProductVariant)

  const initialVariant = mandatoryToPickVariant
    ? null // It's mandatory to pick a variant by user
    : mainInStockVariant || inStockVariants[0] || fallBackVariant

  return {
    selectedVariantId:
      selectedCheckoutItem?.product.variantId || initialVariant?.id,
    selectedVariantShowingPrice:
      selectedCheckoutItem?.totalPriceInclTax.amount ||
      initialVariant?.variantPrice.unitPrice,
  }
}

function getInitialIngredients(
  productDetails: PurchaseProductDetails,
  modifications?: CheckoutItemModification[],
) {
  const { ingredients } = productDetails

  const ingredientModifications = modifications?.filter(mod => !mod.groupId)

  if (ingredientModifications?.length) {
    return ingredients.map(({ ingredientId, position }) => {
      const ingredientModification = ingredientModifications.find(
        im => im.ingredientId === ingredientId,
      )

      if (ingredientModification) {
        const { modification, replaceId } = ingredientModification

        return {
          id: ingredientId,
          selected: modification !== ModificationType.Remove,
          position,
          replacementId:
            modification === ModificationType.Replace ? replaceId : undefined,
        }
      }

      return {
        id: ingredientId,
        selected: true,
        position,
      }
    })
  }

  return ingredients.map(({ ingredientId, position }) => ({
    id: ingredientId,
    selected: true,
    position,
  }))
}

function getInitialAddonGroups(
  productDetails: PurchaseProductDetails,
  modifications?: CheckoutItemModification[],
  addOnProducts?: CheckoutItem[],
) {
  const { addOnGroups } = productDetails

  const allAddonGroupModifications = modifications?.filter(mod => mod.groupId)

  if (allAddonGroupModifications?.length) {
    return addOnGroups.map(
      ({ addOnGroupId, ingredients, addOnSettings, products }) => {
        const addonGroupModifications = allAddonGroupModifications.filter(
          agm => agm.groupId === addOnGroupId,
        )

        if (addonGroupModifications.length) {
          const { allowMultiple, maximumAmount, minimumAmount } = addOnSettings
          const isRadioButtonTypeAddOn =
            !allowMultiple &&
            maximumAmount &&
            maximumAmount < 2 &&
            minimumAmount &&
            minimumAmount < 2

          const quantity = addonGroupModifications.reduce(
            (acc, agm) => acc + agm.quantity,
            0,
          )

          return {
            addonId: addOnGroupId,
            addOnSettings,
            ingredients: ingredients
              ? ingredients.map(({ id }) => {
                  const addonGroupModificationIngredient =
                    addonGroupModifications.find(agm => agm.ingredientId === id)

                  return {
                    id,
                    selectedCount:
                      addonGroupModificationIngredient?.quantity || 0,
                    freeAmount:
                      addonGroupModificationIngredient?.freeAmount || 0,
                  }
                })
              : [],
            products: products
              ? products.map(({ id }) => {
                  const matched = addOnProducts?.find(
                    aop => aop.product.id === id,
                  )
                  return {
                    id,
                    selectedVariantId: matched?.product.variantId,
                    selectedCount: matched?.quantity || 0,
                    freeAmount: matched?.addOnFreeAmount || 0,
                  }
                })
              : [],
            selectedIngredientId: isRadioButtonTypeAddOn
              ? addonGroupModifications.find(
                  agm => agm.groupId === addOnGroupId,
                )?.ingredientId
              : undefined,
            selectedVariantId: undefined,
            selectedCount: quantity,
          }
        }

        return {
          addonId: addOnGroupId,
          addOnSettings,
          ingredients: ingredients
            ? ingredients.map(({ id }) => ({
                id,
                selectedCount: 0,
                freeAmount: 0,
              }))
            : [],
          products: products
            ? products.map(({ id }) => {
                const matched = addOnProducts?.find(
                  aop => aop.product.id === id,
                )
                return {
                  id,
                  selectedVariantId: matched?.product.variantId,
                  selectedCount: matched?.quantity || 0,
                  freeAmount: matched?.addOnFreeAmount || 0,
                }
              })
            : [],
          selectedIngredientId: undefined,
          selectedVariantId: undefined,
          selectedCount: 0,
        }
      },
    )
  }

  return addOnGroups.map(
    ({ addOnGroupId, ingredients, addOnSettings, products }) => ({
      addonId: addOnGroupId,
      addOnSettings,
      ingredients: ingredients
        ? ingredients.map(({ id }) => ({ id, selectedCount: 0, freeAmount: 0 }))
        : [],
      products: products
        ? products.map(({ id }) => {
            const matched = addOnProducts?.find(aop => aop.product.id === id)
            return {
              id,
              selectedVariantId: matched?.product.variantId,
              selectedCount: matched?.quantity || 0,
              freeAmount: matched?.addOnFreeAmount || 0,
            }
          })
        : [],
      selectedIngredientId: undefined,
      selectedVariantId: undefined,
      selectedCount: 0,
    }),
  )
}

/** Every required add-on group starts open */
function getInitialExpandedAccordionIds(
  productDetails: PurchaseProductDetails,
) {
  return productDetails.addOnGroups.flatMap(({ addOnSettings }, index) =>
    isAddonGroupRequired(addOnSettings)
      ? [ProductConfigureAccordionId.AddonGroup(index)]
      : [],
  )
}

/** How long the section that needs a choice is highlighted */
const AttentionDurationMs = 600

type ProductConfigureFormProps = {
  onDismiss: () => void
  onSubmit: (values: ConfigureProductFormState) => void
}

export default function ProductConfigureForm({
  onDismiss,
  onSubmit,
}: ProductConfigureFormProps) {
  const contentContainerRef = useRef<HTMLDivElement>(null)

  const [showHeader, setShowHeader] = useState(false)
  const attentionTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const productDetails = useAppSelector(productDetailsSelector)!
  const outletId = useAppSelector(outletSelectedOutletIdSelector)!
  const checkoutOutletId = useAppSelector(
    checkoutCurrentCheckoutOutletIdSelector,
  )!
  const selectedCheckoutItem = useAppSelector(
    checkoutCurrentCheckoutSelectedItemSelector,
  )
  const { hasIngredients, hasVariants, hasAddOnGroups } = productDetails

  const hasProductConfigurations =
    hasVariants || hasIngredients || hasAddOnGroups

  const form = useForm<ConfigureProductFormState>({
    defaultValues: {
      activeAccordionId: getInitialActiveAccordionId(productDetails),
      expandedAccordionIds: getInitialExpandedAccordionIds(productDetails),
      attentionAccordionId: null,
      selectedIngredients: getInitialIngredients(
        productDetails,
        selectedCheckoutItem?.modifications,
      ),
      selectedAddons: getInitialAddonGroups(
        productDetails,
        selectedCheckoutItem?.modifications,
        selectedCheckoutItem?.addOnProducts,
      ),
      quantity: selectedCheckoutItem ? selectedCheckoutItem.quantity : 1,
      outletId: selectedCheckoutItem ? checkoutOutletId : outletId,
      ...getInitialVariantState(productDetails, outletId, selectedCheckoutItem),
    },
  })
  const { handleSubmit, getValues, setValue } = form

  /** Opens the first section that needs a choice and draws attention to it */
  function handleInvalid(errors: FieldErrors<ConfigureProductFormState>) {
    const invalidSection = getFirstInvalidProductSection(
      errors,
      productDetails.addOnGroups,
    )

    if (!invalidSection) {
      return
    }

    const { sectionId } = invalidSection

    if (sectionId === ProductConfigureAccordionId.Variants) {
      setValue('activeAccordionId', sectionId)
    } else {
      const expandedIds = getValues('expandedAccordionIds') ?? []

      if (!expandedIds.includes(sectionId)) {
        setValue('expandedAccordionIds', [...expandedIds, sectionId])
      }
    }

    setValue('attentionAccordionId', sectionId)

    clearTimeout(attentionTimeoutRef.current)
    attentionTimeoutRef.current = setTimeout(() => {
      setValue('attentionAccordionId', null)
    }, AttentionDurationMs)

    const sectionHeader =
      contentContainerRef.current?.querySelector<HTMLElement>(
        `[data-product-section="${sectionId}"]`,
      )

    sectionHeader?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    // Keyboard and screen reader users land on the missing choice too. The
    // scroll above already brings it into view, smoothly.
    sectionHeader?.focus({ preventScroll: true })
  }

  useEffect(() => () => clearTimeout(attentionTimeoutRef.current), [])

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    function handleScroll() {
      if (contentContainer) {
        const scrollTop = contentContainer.scrollTop ?? 0
        setShowHeader(scrollTop > 240)
      }
    }

    if (contentContainer) {
      contentContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (contentContainer) {
        contentContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...form}
    >
      <form onSubmit={handleSubmit(onSubmit, handleInvalid)}>
        <div className={styles.container} ref={contentContainerRef}>
          <Button
            variant="secondary"
            onClick={onDismiss}
            data-testid={AppTestIds.Order.Modal.ProductDetailsModal.CloseButton}
          >
            <CloseIcon />
          </Button>

          <ProductMetaForm showFloatingHeader={showHeader} />
          {hasProductConfigurations && (
            <div>
              {hasVariants && <ProductVariantsForm />}
              {hasIngredients && <ProductIngredientsForm />}
              {hasAddOnGroups && <ProductAddonGroupsForm />}
            </div>
          )}
          <ProductFooterForm />
        </div>
      </form>
    </FormProvider>
  )
}
